"use client";

import { useQueryClient } from "@tanstack/react-query";
import { compareDesc } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpDown, File, HelpCircle, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useJourneyData } from "@/hooks/use-journey-data";
import { authenticatedFetch } from "@/lib/auth";
import { MortgageQuoteDialog } from "./mortgage-quote-dialog";
import { QuoteCard } from "./quote-card";

export function MortgageQuoteManager() {
  const {
    mortgageQuotes = [],
    isLoading,
    addBuyerDocument,
    userProfile,
    journeyData,
  } = useJourneyData();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isAddingQuote, setIsAddingQuote] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "rate" | "payment" | "amount">(
    "date",
  );

  // Add check for active journey
  if (!journeyData?.id) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto space-y-6">
          <div className="bg-primary/5 rounded-2xl p-8 mx-auto">
            <div className="bg-primary/10 rounded-full p-5 w-20 h-20 mx-auto flex items-center justify-center">
              <File className="w-10 h-10 text-primary" />
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900">
              No Active Journey
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              You need to create or select a journey before you can manage
              mortgage quotes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Memoize filtered and sorted quotes
  const filteredQuotes = useMemo(() => {
    return (mortgageQuotes ?? [])
      .filter((quote) =>
        quote.lender_name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .sort((a, b) => {
        switch (sortBy) {
          case "rate":
            // Sort by interest rate, then APR if rates are equal
            if (a.interest_rate === b.interest_rate) {
              return (a.apr ?? 0) - (b.apr ?? 0);
            }
            return a.interest_rate - b.interest_rate;

          case "payment": {
            // Sort by total monthly cost (payment + PMI)
            const totalPaymentA = a.monthly_payment + (a.monthly_pmi ?? 0);
            const totalPaymentB = b.monthly_payment + (b.monthly_pmi ?? 0);
            return totalPaymentA - totalPaymentB;
          }

          case "amount":
            // Sort by loan amount, then total closing costs
            if (a.loan_amount === b.loan_amount) {
              return (
                (a.total_closing_costs ?? 0) - (b.total_closing_costs ?? 0)
              );
            }
            return a.loan_amount - b.loan_amount;

          default:
            // Use date-fns for proper date comparison
            return compareDesc(
              new Date(a.created_at ?? 0),
              new Date(b.created_at ?? 0),
            );
        }
      });
  }, [mortgageQuotes, searchTerm, sortBy]);

  const handleDocumentUploaded = async (docId: string | null) => {
    if (!docId || !userProfile?.id) return;

    try {
      // First link the document to the buyer
      await addBuyerDocument.mutateAsync({
        document_id: docId,
        user_id: userProfile.id,
        reference_id: "loan_estimate",
        journey_id: journeyData.id,
      });

      // Then extract data from the document
      const extractResponse = await authenticatedFetch(
        `/api/mortgage-quotes/extract?documentId=${docId}`,
        { method: "POST" },
      );

      const data = await extractResponse.json();
      if (!extractResponse.ok || !data.success) {
        throw new Error(data.error || "Failed to process document");
      }

      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ["journey"] });

      // Close the dialog
      setIsAddingQuote(false);
      setEditingQuoteId(null);

      toast({
        title: "Quote Added",
        description: "Your mortgage quote has been successfully added",
      });
    } catch (error) {
      console.error("Error processing document:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to process document. Please try again or enter details manually.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (quoteId: string) => {
    setEditingQuoteId(quoteId);
    setIsAddingQuote(true);
  };

  if (isLoading) {
    return <Skeleton className="w-full h-[400px]" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex-1 flex flex-col sm:flex-row gap-2 ">
        <div className="relative flex-1">
          <Input
            placeholder="Search lenders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
        </div>
        <div className="mb-2 md:mb-0 md:mr-2">
          <Select
            value={sortBy}
            onValueChange={(value: typeof sortBy) => setSortBy(value)}
          >
            <SelectTrigger className="md:w-[200px]">
              <ArrowUpDown className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Newest First</SelectItem>
              <SelectItem value="rate">Best Rate</SelectItem>
              <SelectItem value="payment">Lowest Payment</SelectItem>
              <SelectItem value="amount">Loan Amount</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button onClick={() => setIsAddingQuote(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Quote
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon">
                <HelpCircle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">
                Upload your Loan Estimate documents and we'll automatically
                extract the key details. You can review and edit the information
                before saving.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {filteredQuotes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center py-12"
          >
            <div className="max-w-sm mx-auto space-y-4">
              <h3 className="text-lg font-semibold">No mortgage quotes yet</h3>
              <p className="text-muted-foreground">
                Upload your Loan Estimate documents and we'll help you track and
                compare mortgage offers.
              </p>
              <Button onClick={() => setIsAddingQuote(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Your First Quote
              </Button>
            </div>
          </motion.div>
        ) : (
          <ScrollArea className="h-[600px] pr-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredQuotes.map((quote) => (
                <QuoteCard key={quote.id} quote={quote} onEdit={handleEdit} />
              ))}
            </div>
          </ScrollArea>
        )}
      </AnimatePresence>

      <MortgageQuoteDialog
        open={isAddingQuote}
        onOpenChange={(open) => {
          setIsAddingQuote(open);
          if (!open) setEditingQuoteId(null);
        }}
        quoteId={editingQuoteId ?? undefined}
        onDocumentUploaded={handleDocumentUploaded}
      />
    </div>
  );
}
